import { kritisOn } from './policy';
import { dbGetAll } from './db';
import { OLC_RE, olcDecode, olcRecover, olcIsFull } from './olc';

const BASE = 'https://nominatim.openstreetmap.org';
const RECENT_KEY = 'giss_addr_recent';

const PHOTON = 'https://photon.komoot.io';
const fetchT = (url, ms = 4500) => { const c = new AbortController(), id = setTimeout(() => c.abort(), ms); return fetch(url, { signal: c.signal }).finally(() => clearTimeout(id)); };
const phName = (p) => [[p.street || p.name, p.housenumber].filter(Boolean).join(' '), [p.postcode, p.city || p.town || p.village || (p.street ? '' : p.state)].filter(Boolean).join(' '), p.country].filter(Boolean).join(', ');
const phLang = (l) => (['de', 'en', 'fr', 'it'].includes(l) ? l : 'en');

// KRITIS: search only in own data (projects, reports, objects) - nothing leaves the device
async function localSearch(q) {
  const s = q.trim().toLowerCase(), hits = [];
  if (s.length < 2) return hits;
  const add = (lat, lon, name, ...txt) => { if (lat != null && lon != null && txt.some((x) => String(x || '').toLowerCase().includes(s))) hits.push({ lat: +lat, lon: +lon, name }); };
  (await dbGetAll()).forEach((p) => {
    add(p.lat, p.lon, `📁 ${p.title}${p.address ? ` · ${p.address}` : ''}`, p.title, p.address, p.description);
    (p.reports || []).forEach((r) => add(r.lat, r.lon, `📄 ${r.title}${r.address ? ` · ${r.address}` : ''} (${p.title})`, r.title, r.address));
    (p.fittings || []).forEach((f) => add(f.lat, f.lon, `📍 ${f.name || f.type} (${p.title})`, f.name, f.note, f.sub));
  });
  return hits.slice(0, 12);
}

const nomi = async (q, lang) => {
  const res = await fetchT(`${BASE}/search?format=jsonv2&limit=6&q=${encodeURIComponent(q)}&accept-language=${lang}`);
  if (!res.ok) throw new Error(`nominatim ${res.status}`);
  return (await res.json()).map((r) => ({ lat: +r.lat, lon: +r.lon, name: r.display_name }));
};
const photon = async (q, lang) => ((await (await fetchT(`${PHOTON}/api/?limit=6&lang=${phLang(lang)}&q=${encodeURIComponent(q)}`)).json()).features || []).map((f) => ({ lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0], name: phName(f.properties || {}) }));
const ABBR = { dr: 'Drive', st: 'Street', rd: 'Road', ave: 'Avenue', av: 'Avenue', ln: 'Lane', pl: 'Place', sq: 'Square', ct: 'Court', cres: 'Crescent', blvd: 'Boulevard', hwy: 'Highway', 'str.': 'Straße', 'str': 'Straße' };
// Google-style addresses: drop unit/shop/floor parts and letter postcodes (Eircode, UK), expand street abbreviations
const UNIT = /^(units?|unit\s*no|apt|apartment|flat|suite|shop|store|floor|level|wohnung|whg|top|stiege|etage|c\/o|bldg|building)\b|^\d+\s*(&|and|-|\/)\s*\d+$/i;
const LPC = /\b([A-Z]\d{2}\s?[A-Z0-9]{4}|[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2})\b/gi;
export function addrVariants(q) {
  const seg = q.split(',').map((x) => x.replace(LPC, '').replace(/\b([A-Za-z]+\.?)$/, (w) => ABBR[w.toLowerCase()] || w).replace(/\s+/g, ' ').trim()).filter((x) => x && !UNIT.test(x));
  const out = [seg.join(', ')];
  if (seg.length > 3) out.push([seg[0], ...seg.slice(-2)].join(', '));
  for (let k = 1; k < seg.length - 2 && k <= 3; k++) out.push(seg.slice(k).join(', '));
  return [...new Set(out)].filter((x) => x && x !== q);
}

// plus code (full, or short with locality / reference point) -> one hit
async function plusSearch(m, lang, ref) {
  const code = m[1].toUpperCase();
  if (olcIsFull(code)) return [{ ...olcDecode(code), name: code }];
  let r = ref, where = '';
  if (m[2]) { const h = (await geoSearch(m[2], lang))[0]; if (h) { r = h; where = ` · ${h.name.split(',').slice(0, 2).join(',')}`; } }
  return r ? [{ ...olcRecover(code, r.lat, r.lon), name: `${code}${where}` }] : [];
}

// plus codes locally; addresses: Nominatim, Photon (fuzzy), then simplified variants; approximate hits marked with ≈
const uniq = (r) => r.filter((h, i) => r.findIndex((x) => x.name === h.name) === i);
const mapRef = () => { const m = window.__gissMapCenter?.(); return m ? { lat: m.lat, lon: m.lng } : null; };
export async function geoSearch(q, lang, ref) { return uniq(await geoFind(q, lang, ref)); }
async function geoFind(q, lang, ref = mapRef()) {
  if (kritisOn()) return localSearch(q);
  const m = OLC_RE.exec(q.trim());
  if (m) return plusSearch(m, lang, ref);
  let nomOk = true;
  try { const r = await nomi(q, lang); if (r.length) return r; } catch { nomOk = false; }
  const V = addrVariants(q), approx = (r) => r.map((h) => ({ ...h, name: `≈ ${h.name}` }));
  for (const v of [q, ...V].slice(0, 3)) { try { const r = await photon(v, lang); if (r.length) return v === q ? r : approx(r); } catch { /* next */ } }
  if (nomOk) for (const v of V.slice(0, 4)) { try { const r = await nomi(v, lang); if (r.length) return approx(r); } catch { break; } }
  return [];
}

// short "street nr, postcode city" label; null when offline/failed
export async function geoReverse(lat, lon, lang) {
  if (!navigator.onLine || kritisOn()) return null;
  try {
    const r = await (await fetchT(`${BASE}/reverse?format=jsonv2&zoom=18&addressdetails=1&lat=${lat}&lon=${lon}&accept-language=${lang}`)).json();
    const a = r.address || {};
    const street = [a.road || a.pedestrian || a.path, a.house_number].filter(Boolean).join(' ');
    const city = [a.postcode, a.city || a.town || a.village || a.municipality].filter(Boolean).join(' ');
    return [street, city].filter(Boolean).join(', ') || r.display_name || null;
  } catch {
    try {
      const f = (await (await fetchT(`${PHOTON}/reverse?lat=${lat}&lon=${lon}&lang=${phLang(lang)}`)).json()).features?.[0];
      return f ? phName(f.properties || {}) : null;
    } catch { return null; }
  }
}

export const getRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } };
export const addRecent = (h) => {
  const list = [h, ...getRecent().filter((x) => x.name !== h.name)].slice(0, 5);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  return list;
};
export const clearRecent = () => localStorage.removeItem(RECENT_KEY);
